import * as React from 'react';
import { useRouterState, Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';

import { UserMenu } from './user-menu';
import type { SessionInfo } from '@/lib/auth';

export interface TopbarProps {
  session: SessionInfo;
}

/**
 * Topbar — breadcrumbs (left) + user menu (right).
 *
 * Breadcrumbs are derived from the matched route hierarchy. We use the
 * route's `staticData.crumb` (when set) for human labels; the leaf
 * pathname is the fallback. Routes register their crumb in the route
 * definition: `staticData: { crumb: 'Pulpit' }`.
 */
export function Topbar({ session }: TopbarProps) {
  const matches = useRouterState({
    select: (s) =>
      s.matches.filter((m) => {
        const crumb = (m.staticData as { crumb?: string } | undefined)?.crumb;
        return crumb !== undefined;
      }),
  });

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-bg px-6">
      <nav aria-label="Okruszki" className="flex items-center gap-1 text-sm">
        {matches.length === 0 ? (
          <span className="text-text-subtle">Pulpit</span>
        ) : (
          matches.map((match, i) => {
            const crumb = (match.staticData as { crumb?: string }).crumb!;
            const isLast = i === matches.length - 1;
            return (
              <React.Fragment key={match.id}>
                {i > 0 ? (
                  <ChevronRight className="size-3.5 text-text-subtle" aria-hidden="true" />
                ) : null}
                {isLast ? (
                  <span className="font-medium text-text">{crumb}</span>
                ) : (
                  <Link to={match.pathname} className="text-text-muted hover:text-text">
                    {crumb}
                  </Link>
                )}
              </React.Fragment>
            );
          })
        )}
      </nav>

      <UserMenu session={session} />
    </header>
  );
}
